import { useCallback, useRef, useState } from 'react';
import { Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import type { FieldProvenance } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CornersOutIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { KeyedPdfDocument } from '@/components/common/KeyedPdfDocument';
import { BoundingBoxOverlay } from './BoundingBoxOverlay';
import { BoxVisibilityToggle } from './BoxVisibilityToggle';
// Sets pdfjs.GlobalWorkerOptions.workerSrc to the bundled worker asset.
import '@/lib/pdf-worker';

interface PdfDocumentLike {
  numPages: number;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;
const clampScale = (s: number): number =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(s * 100) / 100));

/** Bucket boxes by 1-based page; null-page boxes fall back to page 1. */
function groupByPage(boxes: FieldProvenance[]): Map<number, FieldProvenance[]> {
  const byPage = new Map<number, FieldProvenance[]>();
  for (const box of boxes) {
    const page = box.page ?? 1;
    const list = byPage.get(page) ?? [];
    list.push(box);
    byPage.set(page, list);
  }
  return byPage;
}

/**
 * Renders a source PDF with its extraction bounding boxes overlaid, plus a
 * toolbar for zoom / fit / page navigation / open-in-new-tab. All pages stack
 * vertically; each page wrapper is sized to `width * scale` and the overlay sits
 * `inset-0` over the page canvas so the normalised boxes line up at any zoom.
 */
export function ProvenancePdfViewer({
  url,
  boxes,
  width,
  onUrlExpired,
  labelMode,
  selectable,
  showToolbar = false,
  showBoxes = true,
  onToggleBoxes,
}: {
  url: string;
  boxes: FieldProvenance[];
  width: number;
  onUrlExpired?: () => void;
  labelMode?: 'always' | 'hover';
  selectable?: boolean;
  /** Show the zoom / fit / page-nav / open-in-new-tab toolbar. */
  showToolbar?: boolean;
  /** When false, the bounding-box overlays are not rendered. */
  showBoxes?: boolean;
  /** When provided, a show/hide-boxes toggle appears in the toolbar. */
  onToggleBoxes?: () => void;
}) {
  const t = useGT();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const byPage = groupByPage(boxes);
  const pageWidth = Math.round(width * scale);

  const onLoadError = useCallback(
    (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      if (
        onUrlExpired &&
        (message.includes('403') || message.includes('AccessDenied'))
      ) {
        onUrlExpired();
      }
    },
    [onUrlExpired],
  );

  const goToPage = useCallback((page: number, total: number) => {
    const next = Math.min(Math.max(page, 1), total);
    setCurrentPage(next);
    pageRefs.current
      .get(next)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  const total = numPages ?? 0;

  return (
    <div className="flex flex-col gap-2">
      {showToolbar && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-md border bg-background/95 px-1.5 py-1 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Previous page')}
            disabled={total <= 1 || currentPage <= 1}
            onClick={() => goToPage(currentPage - 1, total)}
          >
            <CaretLeftIcon className="size-4" />
          </Button>
          <span className="min-w-16 text-center text-xs text-muted-foreground tabular-nums">
            {total > 0 ? `${currentPage} / ${total}` : '—'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Next page')}
            disabled={total <= 1 || currentPage >= total}
            onClick={() => goToPage(currentPage + 1, total)}
          >
            <CaretRightIcon className="size-4" />
          </Button>

          <span className="mx-1 h-4 w-px bg-border" />

          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Zoom out')}
            disabled={scale <= MIN_SCALE}
            onClick={() => setScale((s) => clampScale(s - SCALE_STEP))}
          >
            <MagnifyingGlassMinusIcon className="size-4" />
          </Button>
          <span className="min-w-10 text-center text-xs text-muted-foreground tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Zoom in')}
            disabled={scale >= MAX_SCALE}
            onClick={() => setScale((s) => clampScale(s + SCALE_STEP))}
          >
            <MagnifyingGlassPlusIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Fit to width')}
            disabled={scale === 1}
            onClick={() => setScale(1)}
          >
            <CornersOutIcon className="size-4" />
          </Button>

          <span className="mx-1 h-4 w-px bg-border" />

          {onToggleBoxes && (
            <BoxVisibilityToggle pressed={showBoxes} onToggle={onToggleBoxes} />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={t('Open in new tab')}
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          >
            <ArrowSquareOutIcon className="size-4" />
          </Button>
        </div>
      )}

      <div className="overflow-auto">
        <KeyedPdfDocument
          url={url}
          onLoadSuccess={(pdf: PdfDocumentLike) => setNumPages(pdf.numPages)}
          onLoadError={onLoadError}
          loading={
            <div className="flex min-h-[300px] items-center justify-center">
              <div className="size-6 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          }
          error={
            <div className="py-8 text-center text-sm text-destructive">
              <T>Could not load the source document.</T>
            </div>
          }
        >
          <div className="mx-auto flex w-max flex-col gap-3">
            {Array.from({ length: numPages ?? 0 }, (_, i) => {
              const pageNumber = i + 1;
              return (
                <div
                  key={pageNumber}
                  ref={(el) => {
                    if (el) pageRefs.current.set(pageNumber, el);
                    else pageRefs.current.delete(pageNumber);
                  }}
                  className="relative shadow-sm"
                  style={{ width: pageWidth }}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  {showBoxes && (
                    <BoundingBoxOverlay
                      boxes={byPage.get(pageNumber) ?? []}
                      labelMode={labelMode}
                      selectable={selectable}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </KeyedPdfDocument>
      </div>
    </div>
  );
}
